import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { onMessage } from "firebase/messaging";
import { toast } from "react-toastify";
import { useAuth } from "./AuthenticateProvider";
import { messaging } from "../Helpers/firebase";
import { fetchNotifications, fetchUnreadCount, markNotificationsRead } from "../Helpers/notificationApi";
import { askPermission, permissionState, registerThisDevice } from "../Helpers/pushDevice";
import { msSinceSound, playBellSound, playChime } from "../Helpers/ShowNotification";

const NotificationContext = createContext(null);

// Urgent events get the loud bell while the app is open; every other notification gets a soft chime. A message with no event is an older task-alarm push.
const SOUND_EVENTS = new Set(["task.alarm", "task.alarm_escalated", "task.overdue_unassigned"]);
const POLL_MS = 60 * 1000;
const NAVIGATE_EVENT = "gmd:navigate";

export const openLink = (url) => {
    if (typeof url === "string" && /^\/(?!\/)/.test(url)) window.dispatchEvent(new CustomEvent(NAVIGATE_EVENT, { detail: url }));
};

// Call once inside the router: moves the app to a link asked for by a toast or by a click on
// a push notification while the app was already open.
export const useNotificationNavigation = (navigate) => {
    useEffect(() => {
        const onNavigate = (event) => navigate(event.detail);
        window.addEventListener(NAVIGATE_EVENT, onNavigate);
        return () => window.removeEventListener(NAVIGATE_EVENT, onNavigate);
    }, [navigate]);
};

function NotificationProvider({ children }) {
    const { isAuthenticated } = useAuth();
    const [unread, setUnread] = useState(0);
    const [latest, setLatest] = useState([]);
    const [permission, setPermission] = useState(permissionState());
    const askedRef = useRef(false);
    const unreadRef = useRef(null);

    // The count only ever goes up when something new arrived. A push that already rang a moment
    // ago is not rung twice; the very first count after sign-in is silent.
    const chimeIfNew = (count) => {
        const before = unreadRef.current;
        unreadRef.current = count;
        if (before !== null && count > before && msSinceSound() > 4000) playChime();
    };

    const refresh = useCallback(async () => {
        const data = await fetchNotifications({ limit: 10 });
        if (data) {
            setLatest(data.items || []);
            setUnread(data.unread || 0);
            chimeIfNew(data.unread || 0);
        }
    }, []);

    const refreshCount = useCallback(async () => {
        const data = await fetchUnreadCount();
        if (data) {
            setUnread(data.unread || 0);
            chimeIfNew(data.unread || 0);
        }
    }, []);

    // This browser as a device: ask once per visit if never asked, then keep its token saved.
    useEffect(() => {
        if (!isAuthenticated || permissionState() === "unsupported") return;
        (async () => {
            let state = permissionState();
            if (state === "default" && !askedRef.current) {
                askedRef.current = true;
                state = await askPermission();
            }
            setPermission(state);
            if (state === "granted") await registerThisDevice();
        })();
    }, [isAuthenticated]);

    // App open: a push arrives as a toast, the bell updates, and urgent task alerts ring.
    useEffect(() => {
        if (!isAuthenticated || !messaging) return undefined;
        const unsubscribe = onMessage(messaging, (payload) => {
            const data = payload?.data || {};
            const title = data.title || payload?.notification?.title || "New notification";
            const body = data.body || payload?.notification?.body || "";
            toast.info(body ? `${title} - ${body}` : title, { autoClose: 6000, onClick: () => openLink(data.url) });
            if (!data.event || SOUND_EVENTS.has(data.event)) playBellSound();
            else playChime();
            refresh();
        });
        return () => unsubscribe?.();
    }, [isAuthenticated, refresh]);

    // The bell stays right even when a push never arrives (other device read it, browser asleep).
    useEffect(() => {
        if (!isAuthenticated) {
            setUnread(0);
            setLatest([]);
            unreadRef.current = null;
            return undefined;
        }
        refresh();
        const timer = setInterval(() => { if (!document.hidden) refreshCount(); }, POLL_MS);
        const onVisible = () => { if (!document.hidden) refreshCount(); };
        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("focus", onVisible);
        return () => {
            clearInterval(timer);
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("focus", onVisible);
        };
    }, [isAuthenticated, refresh, refreshCount]);

    // A tap on a push while the app is already open arrives here from the service worker.
    useEffect(() => {
        if (!("serviceWorker" in navigator)) return undefined;
        const onMessageFromWorker = (event) => {
            if (event.data?.type === "gmd-navigate") openLink(event.data.url);
            // A push reached the worker while this page was open but not in front: make the sound here.
            if (event.data?.type === "gmd-sound") {
                if (msSinceSound() > 1500) {
                    if (!event.data.event || SOUND_EVENTS.has(event.data.event)) playBellSound();
                    else playChime();
                }
                refreshCount();
            }
        };
        navigator.serviceWorker.addEventListener("message", onMessageFromWorker);
        return () => navigator.serviceWorker.removeEventListener("message", onMessageFromWorker);
    }, [refreshCount]);

    const markRead = useCallback(async (ids) => {
        const list = (Array.isArray(ids) ? ids : [ids]).filter(Boolean);
        if (!list.length) return;
        setLatest((items) => items.map((item) => (list.includes(item.notificationId) ? { ...item, readAt: item.readAt || new Date().toISOString() } : item)));
        const result = await markNotificationsRead({ ids: list });
        if (result) { setUnread(result.unread || 0); unreadRef.current = result.unread || 0; }
        else refreshCount();
    }, [refreshCount]);

    const markAllRead = useCallback(async () => {
        setLatest((items) => items.map((item) => ({ ...item, readAt: item.readAt || new Date().toISOString() })));
        setUnread(0);
        unreadRef.current = 0;
        await markNotificationsRead({ all: true });
        refreshCount();
    }, [refreshCount]);

    // Must come from a click: browsers refuse a permission prompt that the person did not trigger.
    const enableBrowserAlerts = useCallback(async () => {
        const state = await askPermission();
        setPermission(state);
        if (state === "granted") await registerThisDevice();
        return state;
    }, []);

    const value = { unread, latest, permission, refresh, refreshCount, markRead, markAllRead, enableBrowserAlerts };
    return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export default NotificationProvider;

export const useNotifications = () => useContext(NotificationContext) || {
    unread: 0, latest: [], permission: "unsupported", refresh() {}, refreshCount() {}, markRead() {}, markAllRead() {}, enableBrowserAlerts() {},
};
